"use client";

import { api } from "@convex/_generated/api";
import { useQuery } from "convex/react";
import { Grid3x3 } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

export function WeaponsSidebar() {
  const weapons = useQuery(api.loadouts.queries.getWeapons);
  const pathname = usePathname();

  if (!weapons) {
    return <WeaponsSidebarSkeleton />;
  }

  // Group weapons by category
  const weaponsByCategory = weapons.reduce(
    (acc, weapon) => {
      if (!acc[weapon.category]) {
        acc[weapon.category] = [];
      }
      acc[weapon.category].push(weapon);
      return acc;
    },
    {} as Record<string, typeof weapons>
  );

  const categories = Object.keys(weaponsByCategory).sort();
  const isOnWeaponsPage = pathname === "/weapons";

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#0C0D0E]">
      <div className="border-[#1c1f22] border-b px-4 py-3 font-mono text-[#F5A23E] text-[11px] tracking-[0.2em]">
        {"// ARSENAL"}
      </div>
      <div className="flex flex-col gap-5 p-4">
        {/* All Weapons Link */}
        <Link
          className={`flex items-center gap-2 rounded-[5px] px-3 py-2 font-mono text-xs tracking-wider transition-colors ${
            isOnWeaponsPage
              ? "bg-[#F5A23E] font-bold text-[#0a0b0c]"
              : "border border-[#262A2D] text-[#A9AFB3] hover:border-[#F5A23E]/50 hover:text-[#EAEBEC]"
          }`}
          href="/weapons"
        >
          <Grid3x3 className="h-4 w-4" />
          ALL WEAPONS
        </Link>

        {/* Categories */}
        {categories.map((category) => {
          const categoryWeapons = weaponsByCategory[category];

          return (
            <div className="flex flex-col gap-2" key={category}>
              <div className="flex items-center justify-between">
                <h3 className="font-mono text-[#8B9297] text-[11px] tracking-[0.15em]">
                  {category.toUpperCase()}
                </h3>
                <span className="rounded-[3px] border border-[#262A2D] bg-[#141618] px-1.5 py-0.5 font-mono text-[#6a7075] text-[10px]">
                  {categoryWeapons.length}
                </span>
              </div>

              <div className="flex flex-col gap-1">
                {categoryWeapons.map((weapon) => {
                  const weaponPath = `/weapons/${weapon.slug}`;
                  const isActive = pathname === weaponPath;

                  return (
                    <Link
                      className={`flex items-center justify-between rounded-[5px] px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "bg-[#F5A23E]/10 font-medium text-[#F5A23E]"
                          : "text-[#8B9297] hover:bg-[#141618] hover:text-[#EAEBEC]"
                      }`}
                      href={weaponPath}
                      key={weapon._id}
                    >
                      <span className="truncate">{weapon.name}</span>
                      {weapon.unlockRank ? (
                        <span className="ml-2 shrink-0 rounded-[3px] border border-[#262A2D] px-1.5 py-0.5 font-mono text-[#6a7075] text-[10px]">
                          {weapon.unlockRank}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeaponsSidebarSkeleton() {
  return (
    <div className="overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#0C0D0E]">
      <div className="border-[#1c1f22] border-b px-4 py-3 font-mono text-[#F5A23E] text-[11px] tracking-[0.2em]">
        {"// ARSENAL"}
      </div>
      <div className="flex flex-col gap-5 p-4">
        <div className="h-9 w-full animate-pulse rounded-[5px] bg-[#141618]" />
        {Array.from({ length: 3 }).map((_category, categoryIdx) => (
          <div
            className="flex flex-col gap-2"
            key={`category-${categoryIdx.toString()}`}
          >
            <div className="h-4 w-24 animate-pulse rounded bg-[#141618]" />
            <div className="flex flex-col gap-1">
              {Array.from({ length: 4 }).map((_weapon, weaponIdx) => (
                <div
                  className="h-9 w-full animate-pulse rounded-[5px] bg-[#141618]"
                  key={`weapon-${categoryIdx.toString()}-${weaponIdx.toString()}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
